import { useCallback, useEffect, useState } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import pushService from '../../services/pushService';
import Button from '../Common/Button';

const STATUS = {
  enabled: { badge: 'badge-success', ar: 'مفعّلة', en: 'Enabled' },
  disabled: { badge: 'badge-neutral', ar: 'معطّلة', en: 'Disabled' },
  denied: { badge: 'badge-danger', ar: 'تم رفض الإذن', en: 'Permission denied' },
  unsupported: { badge: 'badge-neutral', ar: 'غير مدعومة', en: 'Unsupported' },
  'needs-install': { badge: 'badge-warning', ar: 'يتطلب التثبيت', en: 'Install required' },
  unconfigured: { badge: 'badge-warning', ar: 'غير مهيأة على الخادم', en: 'Not configured on server' },
};

const NotificationSettings = () => {
  const { isRtl } = useLocalization();
  const t = (ar, en) => (isRtl ? ar : en);

  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const refresh = useCallback(async () => setState(await pushService.getState()), []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const run = async (action) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await action();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('حدث خطأ', 'Something went wrong'));
    } finally {
      await refresh();
      setBusy(false);
    }
  };

  // The permission prompt must start synchronously from the tap, so enable() is called first in the handler.
  const handleEnable = () => run(() => pushService.enable());
  const handleDisable = () => run(() => pushService.disable());
  const handleTest = () =>
    run(async () => {
      const r = await pushService.sendTest();
      setInfo(
        r.sent > 0
          ? t('تم إرسال إشعار تجريبي.', 'Test notification sent.')
          : t('لم يتم إرسال أي إشعار. فعّل الإشعارات على هذا الجهاز أولاً.', 'Nothing was sent. Enable notifications on this device first.'),
      );
    });

  const status = state ? STATUS[state] : null;

  return (
    <div className="card" style={{ marginBottom: 'var(--sp-6)' }}>
      <div className="card-header">
        <h2 className="card-title">{t('إشعارات هذا الجهاز', 'Device notifications')}</h2>
        {status && <span className={`badge ${status.badge}`}>{isRtl ? status.ar : status.en}</span>}
      </div>
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <p style={{ margin: 0 }}>
          {t(
            'استلم إشعاراً فورياً عند وصول طلب جديد، حتى عندما تكون لوحة التحكم مغلقة.',
            'Get an instant notification when a new order arrives, even when the dashboard is closed.',
          )}
        </p>

        {state === 'needs-install' && (
          <div>
            <strong>
              {t(
                'لاستلام الإشعارات على iPhone، أضف لوحة التحكم إلى الشاشة الرئيسية أولاً.',
                'To receive notifications on iPhone, add this dashboard to your Home Screen first.',
              )}
            </strong>
            <ol style={{ margin: 'var(--sp-2) 0 0', paddingInlineStart: 'var(--sp-5)' }}>
              <li>{t('افتح هذه الصفحة في Safari.', 'Open this page in Safari.')}</li>
              <li>{t('اضغط زر المشاركة (المربع مع السهم).', 'Tap the Share button (square with arrow).')}</li>
              <li>{t('اختر «إضافة إلى الشاشة الرئيسية».', 'Choose “Add to Home Screen”.')}</li>
              <li>{t('افتح التطبيق من الشاشة الرئيسية وعد إلى هذه الصفحة.', 'Open the app from your Home Screen and come back to this page.')}</li>
            </ol>
          </div>
        )}

        {state === 'unsupported' && (
          <p style={{ margin: 0 }}>
            {t('هذا المتصفح أو الجهاز لا يدعم الإشعارات.', 'This browser or device does not support push notifications.')}
          </p>
        )}

        {state === 'unconfigured' && (
          <p style={{ margin: 0 }}>
            {t('لم يتم إعداد مفاتيح الإشعارات على الخادم بعد.', 'Push keys have not been configured on the server yet.')}
          </p>
        )}

        {state === 'denied' && (
          <p style={{ margin: 0 }}>
            {t(
              'تم حظر الإشعارات. فعّلها من إعدادات الجهاز أو المتصفح لهذا الموقع ثم أعد المحاولة.',
              'Notifications are blocked. Allow them for this site in your device/browser settings, then try again.',
            )}
          </p>
        )}

        {error && <div className="error-alert" style={{ margin: 0 }}><span>{error}</span></div>}
        {info && !error && <div style={{ color: 'var(--text-secondary)' }}>{info}</div>}

        <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
          {state === 'disabled' && (
            <Button variant="primary" onClick={handleEnable} disabled={busy}>
              {busy ? t('جاري التفعيل...', 'Enabling...') : t('تفعيل الإشعارات', 'Enable Notifications')}
            </Button>
          )}
          {state === 'enabled' && (
            <>
              <Button variant="secondary" onClick={handleTest} disabled={busy}>
                {t('إرسال إشعار تجريبي', 'Send test notification')}
              </Button>
              <Button variant="secondary" onClick={handleDisable} disabled={busy}>
                {t('تعطيل الإشعارات', 'Disable Notifications')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
